/**
 * NutritionInfoTooltip Component
 * Small info icon with tooltip explaining macro estimation accuracy
 */

import { useState, useRef, useCallback } from 'react'
import { ICONS, getIconSize } from '@talktoast/shared'

const TOOLTIP_TEXT = 'Macros are AI-estimated and may vary.'
const TOOLTIP_ID = 'nutrition-tooltip'

export default function NutritionInfoTooltip() {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  const handleMouseLeave = useCallback(() => {
    close()
  }, [close])

  const handleBlur = useCallback(() => {
    close()
  }, [close])

  return (
    <span
      className="relative inline-flex items-center"
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-describedby={isOpen ? TOOLTIP_ID : undefined}
        aria-label="Nutrition info"
        className="inline-flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-toast-gold rounded"
        onClick={toggle}
        onMouseEnter={open}
        onFocus={open}
        onBlur={handleBlur}
      >
        <ICONS.feedback.info className={getIconSize('sm')} />
      </button>

      {isOpen && (
        <div
          id={TOOLTIP_ID}
          role="tooltip"
          className="absolute bottom-full right-0 mb-1.5 z-50 w-max max-w-[min(320px,calc(100vw-2rem))] rounded-md bg-white shadow-md border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 pointer-events-none"
        >
          {TOOLTIP_TEXT}
          {/* Arrow — aligned to right side to match anchor point */}
          <span className="absolute top-full right-2 border-4 border-transparent border-t-gray-200" />
        </div>
      )}
    </span>
  )
}
